/** @jsxImportSource @emotion/react */
import {
  Autocomplete,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { css } from "@emotion/react";
import { Box } from "@mui/system";
import styled from "@emotion/styled";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNewsListDetails,
  getNewsTagDetails,
  getNewsUpdateDetails,
  queryUpdateDetails,
} from "./trainingVideoApiRequest";
import { v4 } from "uuid";
import moment from "moment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinearBufferSpinner from "../../shared/components/spinners/LinearBufferSpinner";
import { MOBLIE_VIEW_WIDTH } from "../../shared/constants/constants";
import { PlayButton } from "../../shared/assets/icons/PlayButton.jsx";
import Markdown from "markdown-to-jsx";

const NewsWrapper = styled(Box)(({ mobileMatches }) => ({
  backgroundColor: "#121212",
  minHeight: "61vh",
  padding: "0 4%",
  ".topBar": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: mobileMatches ? "center" : "flex-start",
    padding: mobileMatches ? "3% 0" : "7% 0",
    flexDirection: mobileMatches ? "row" : "column",

    ".MuiOutlinedInput-input": {
      height: "1.5rem",
    },
    ".MuiFormLabel-root": {
      bottom: "1.5rem",
      top: "unset",
    },
    ".MuiOutlinedInput-root": {
      padding: "unset",
      color: "white",
    },
    ".MuiAutocomplete-listbox": {
      color: "black",
    },
    ".css-10y3i1v-MuiFormLabel-root-MuiInputLabel-root": {
      color: "rgb(118, 118, 118)",
    },
    ".css-wntiqe-MuiFormLabel-root-MuiInputLabel-root": {
      color: "#007aff",
    },
    button: {
      borderRadius: "unset",
      color: "white",
    },
    ".MuiCardActionArea-root": {
      "& > p": {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  },
}));

const cardCss = css`
  margin-bottom: 2%;
  background: black;
  color: white;
  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .MuiTypography-h6 {
    font-size: 1.5rem;
    line-height: 1.334;
  }
`;
const playIcon = css`
  width: 62px;
  height: 62px;
  position: absolute;
  bottom: calc(50% - 31px);
  left: calc(50% - 31px);
`;
const cardMediaWrapper = css`
  position: relative;
`;

const cardWrapperCss = (mobileMatches) => css`
  display: grid;
  grid-template-columns: ${mobileMatches ? "repeat(3, 1fr)" : "1fr"};
  grid-template-rows: auto auto auto;
  row-gap: 12px;
  column-gap: 16px;
  min-height: 21vh;
`;
const descriptionWrapper = css`
  font-size: 14px;
  line-height: 35px;
`;
const TrainingVideo = () => {
  const [newsData, setNewsData] = useState([]);
  const [newsTags, setNewsTags] = useState([]);
  const [pageSize, setPageSize] = useState(1);
  const [filter, setFilter] = useState(null);
  const [pageCount, setPageCount] = useState(null);
  const [loader, setLoader] = useState(false);
  const [newsListingData, setNewsListingData] = useState();

  const mobileMatches = useMediaQuery(MOBLIE_VIEW_WIDTH);
  const navigate = useNavigate();

  useEffect(() => {
    getNewsListDetails().then(({ data }) => {
      document.title = data?.data?.attributes?.title;
      const newsListingDataResponse = data.data.attributes.body[0];
      setNewsListingData(newsListingDataResponse);
    });
  }, []);

  useEffect(() => {
    newsListingData &&
      getNewsTagDetails().then(({ data }) => {
        const newsTagData = data.data;
        newsTagData.unshift({
          attributes: { title: newsListingData?.allFilterOption },
        });
        setNewsTags(newsTagData);

        const trendingTag = newsListingData?.trTag?.data?.attributes?.title;
        setFilter(trendingTag || newsListingData?.allFilterOption);
        setLoader(true);
        trendingTag
          ? queryUpdateDetails(trendingTag, 1)
              .then(({ data }) => {
                setNewsData(data.data);
                setPageCount(data.meta.pagination.pageCount);
                setLoader(false);
              })
              .catch((err) => {
                console.log("Error::", err.message);
                setLoader(false);
              })
          : getNewsUpdateDetails(1)
              .then(({ data }) => {
                setNewsData(data.data);
                setPageCount(data.meta.pagination.pageCount);
                setLoader(false);
              })
              .catch((err) => {
                console.log("Error::", err.message);
                setLoader(false);
              });
      });
  }, [newsListingData]);

  const onChangeFunc = ({ target }) => {
    setPageSize(1);
    setFilter(target.innerText);
    setLoader(true);
    if (target.innerText === newsListingData?.allFilterOption) {
      getNewsUpdateDetails(1)
        .then(({ data }) => {
          setNewsData(data.data);
          setPageCount(data.meta.pagination.pageCount);
        })
        .catch((err) => {
          console.log("Error::", err.message);
        });
    } else {
      queryUpdateDetails(target.innerText, 1)
        .then(({ data }) => {
          setNewsData(data.data);
          setPageCount(data.meta.pagination.pageCount);
        })
        .catch((err) => {
          console.log("Error::", err.message);
        });
    }
    setLoader(false);
  };

  const loadMoreClick = () => {
    setLoader(true);
    setPageSize(pageSize + 1);
    if (filter != newsListingData?.allFilterOption) {
      queryUpdateDetails(filter, pageSize + 1).then(({ data }) => {
        setNewsData([...newsData, ...data.data]);
        setPageCount(data.meta.pagination.pageCount);
      });
    } else {
      getNewsUpdateDetails(pageSize + 1).then(({ data }) => {
        const updatedNewData = data.data;
        setNewsData([...newsData, ...updatedNewData]);
        setPageCount(data.meta.pagination.pageCount);
      });
    }
    setLoader(false);
  };

  return (
    <NewsWrapper mobileMatches={mobileMatches}>
      <Box className="topBar">
        <Typography
          variant="h5"
          component="div"
          sx={{
            color: "white",
          }}
        >
          {newsListingData?.title}
        </Typography>
        {filter && (
          <Autocomplete
            disablePortal
            disableClearable
            id="combo-box-demo"
            getOptionLabel={(option) => option.attributes.title}
            options={newsTags}
            sx={{
              width: mobileMatches ? 200 : "100%",
              marginTop: mobileMatches ? 0 : "24px",
            }}
            onChange={onChangeFunc}
            value={{ attributes: { title: filter } }}
            renderInput={(params) => (
              <TextField {...params} label={newsListingData?.filterLabel} />
            )}
          />
        )}
      </Box>
      <Box css={() => cardWrapperCss(mobileMatches)}>
        {newsData &&
          newsData.map(({ attributes }) => (
            <Card css={cardCss} key={v4()}>
              <CardActionArea
                onClick={() =>
                  navigate(`/training-video/training-detail/${attributes.slug}`)
                }
              >
                <Box css={cardMediaWrapper}>
                  <CardMedia
                    height="240px"
                    component="img"
                    image={`${process.env.REACT_APP_BASEURL}${attributes?.thumbnailImage?.data?.attributes?.url}`}
                    alt={`${process.env.REACT_APP_BASEURL}${attributes?.thumbnailImage?.data?.attributes?.url}`}
                  />
                  <PlayButton css={playIcon} />
                </Box>
                <CardContent>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{ fontFamily: (theme) => theme.typography.MBCORPOA }}
                  >
                    {attributes.title}
                  </Typography>
                  <Typography variant="body2" color="#949494">
                    {moment(attributes.updatedAt).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                  <Typography css={descriptionWrapper} variant="body2">
                    <Markdown>{attributes?.description || ""}</Markdown>
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
      </Box>

      {pageSize !== pageCount && (
        <Box
          component="span"
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {newsData.length > 0 && !loader ? (
            <Button
              sx={{ textDecoration: "underline", fontSize: "14px" }}
              onClick={loadMoreClick}
            >
              {newsListingData.loadMore}
              <KeyboardArrowDownIcon
                sx={{ color: "#1976d2", p: 1 }}
                id="rock"
              />
            </Button>
          ) : loader ? (
            <LinearBufferSpinner />
          ) : (
            <Typography variant="h5" color="text.secondary">
              {newsListingData?.noDataFound}
            </Typography>
          )}
        </Box>
      )}
    </NewsWrapper>
  );
};

export default TrainingVideo;
